/**
 * AI Inventory Page (Shadow AI Discovery)
 *
 * Lists the AI infrastructure discovered in a customer's connected AWS
 * account(s), grouped by asset type, with filters and posture-flag badges.
 * A "Scan now" button triggers a discovery scan and shows live status via the
 * polling hook. The empty state routes to the connect wizard.
 *
 * SECURITY: asset names/ARNs/metadata are attacker-controllable customer data.
 * They are rendered as text only (React escapes by default) and length-capped
 * via `truncate`. Never render them as HTML.
 */

import { useMemo, useState } from "react";
import {
  AlertCircle,
  Loader2,
  Plug,
  RefreshCw,
  Search,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AwsDiscoveryConfigDialog } from "@/components/integrations/AwsDiscoveryConfigDialog";
import { AwsDiscoveryAssetDetailSheet } from "@/components/integrations/AwsDiscoveryAssetDetailSheet";
import { PostureFlagBadge } from "@/components/integrations/awsDiscoveryShared";
import { truncate } from "@/components/integrations/awsDiscoveryFormat";
import {
  useAwsDiscoveryAssets,
  useAwsDiscoveryConnections,
  useAwsDiscoveryScan,
  useTriggerAwsDiscoveryScan,
} from "@/hooks/useAwsDiscovery";
import {
  ASSET_TYPE_LABELS,
  ASSET_TYPE_ORDER,
  isTerminalScanStatus,
  postureFlagSeverity,
} from "@/types/awsDiscovery";
import type {
  AssetType,
  DiscoveredAsset,
  PostureSeverity,
  ScanStatus,
} from "@/types/awsDiscovery";
import { ApiError } from "@/services/api";

const ALL = "__all__";

const SCAN_STATUS_LABELS: Record<ScanStatus, string> = {
  queued: "Queued",
  running: "Scanning…",
  completed: "Scan complete",
  partial: "Scan complete (partial)",
  failed: "Scan failed",
};

export function AiInventoryPage() {
  // Filters
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>(ALL);
  const [regionFilter, setRegionFilter] = useState<string>(ALL);
  const [postureFilter, setPostureFilter] = useState<string>(ALL);

  // Sheet + connect dialog
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  // Active scan being polled
  const [activeScanId, setActiveScanId] = useState<number | null>(null);

  const { data: connections, isLoading: connectionsLoading } =
    useAwsDiscoveryConnections();

  const filters = useMemo(
    () => ({
      asset_type: assetTypeFilter === ALL ? undefined : (assetTypeFilter as AssetType),
      region: regionFilter === ALL ? undefined : regionFilter,
      has_posture_flags:
        postureFilter === ALL ? undefined : postureFilter === "flagged",
      items_per_page: 200,
    }),
    [assetTypeFilter, regionFilter, postureFilter]
  );

  const {
    data: assetsResponse,
    isLoading: assetsLoading,
    isError: assetsError,
    error: assetsErrorObj,
  } = useAwsDiscoveryAssets(filters);

  const triggerScan = useTriggerAwsDiscoveryScan();
  const scan = useAwsDiscoveryScan(activeScanId);

  const assets = useMemo(() => assetsResponse?.data ?? [], [assetsResponse]);
  const hasConnection = (connections?.length ?? 0) > 0;
  const isScanning =
    triggerScan.isPending ||
    (!!scan.data && !isTerminalScanStatus(scan.data.status));

  // Regions present in the current dataset, for the region filter.
  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach((a) => set.add(a.region));
    return Array.from(set).sort();
  }, [assets]);

  // Group assets by type for the table, in the canonical order.
  const grouped = useMemo(() => {
    const map = new Map<AssetType, DiscoveredAsset[]>();
    assets.forEach((a) => {
      const list = map.get(a.asset_type) ?? [];
      list.push(a);
      map.set(a.asset_type, list);
    });
    return ASSET_TYPE_ORDER.filter((t) => map.has(t)).map((t) => ({
      type: t,
      items: map.get(t) as DiscoveredAsset[],
    }));
  }, [assets]);

  const handleScanNow = async () => {
    if (!connections || connections.length === 0) return;
    // v1: scan the first connection. (Multi-connection picker is future work.)
    const connectionId = connections[0].id;
    try {
      const result = await triggerScan.mutateAsync(connectionId);
      setActiveScanId(result.id);
    } catch {
      // Error surfaced below via triggerScan.error (e.g. 409 scan in progress).
    }
  };

  const openAsset = (assetId: number) => {
    setSelectedAssetId(assetId);
    setSheetOpen(true);
  };

  const scanErrorMessage =
    triggerScan.error instanceof ApiError && triggerScan.error.status === 409
      ? "A scan is already running for this account. Hang tight."
      : triggerScan.error?.message;

  // --- Empty state: no connection yet ---
  if (!connectionsLoading && !hasConnection) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Plug className="h-5 w-5" />
              <CardTitle>Shadow AI Discovery</CardTitle>
            </div>
            <CardDescription>
              Connect one read-only AWS role and we'll find every AI model, endpoint, and
              agent running in your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-dashed p-8 text-center">
              <h3 className="text-lg font-medium">No AWS account connected</h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Deploy a read-only CloudFormation stack to start discovering Bedrock,
                SageMaker, and AI-key signals. We never read your secret values.
              </p>
              <AwsDiscoveryConfigDialog
                open={connectOpen}
                onOpenChange={setConnectOpen}
                onConnected={() => setConnectOpen(false)}
              >
                <Button className="mt-4" onClick={() => setConnectOpen(true)}>
                  Connect AWS account
                </Button>
              </AwsDiscoveryConfigDialog>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">AI Bill of Materials</h1>
          <p className="text-sm text-muted-foreground">
            Every AI model, endpoint, and agent discovered in your connected AWS account.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeScanId && scan.data && (
            <Badge
              variant={scan.data.status === "failed" ? "destructive" : "secondary"}
              className="flex items-center gap-1"
            >
              {!isTerminalScanStatus(scan.data.status) && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
              {SCAN_STATUS_LABELS[scan.data.status]}
            </Badge>
          )}
          <Button onClick={handleScanNow} disabled={isScanning}>
            {isScanning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scanning…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Scan now
              </>
            )}
          </Button>
        </div>
      </div>

      {scanErrorMessage && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>{scanErrorMessage}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={assetTypeFilter} onValueChange={setAssetTypeFilter}>
          <SelectTrigger className="w-[220px]" aria-label="Filter by asset type">
            <SelectValue placeholder="All asset types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All asset types</SelectItem>
            {ASSET_TYPE_ORDER.map((t) => (
              <SelectItem key={t} value={t}>
                {ASSET_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-[180px]" aria-label="Filter by region">
            <SelectValue placeholder="All regions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All regions</SelectItem>
            {regionOptions.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={postureFilter} onValueChange={setPostureFilter}>
          <SelectTrigger className="w-[200px]" aria-label="Filter by posture flags">
            <SelectValue placeholder="All assets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All assets</SelectItem>
            <SelectItem value="flagged">Has posture flags</SelectItem>
            <SelectItem value="clean">No posture flags</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Body */}
      {assetsLoading || connectionsLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading inventory…
        </div>
      ) : assetsError ? (
        <div className="flex items-start gap-2 py-12 text-sm text-destructive">
          <ShieldAlert className="h-4 w-4 mt-0.5" />
          {assetsErrorObj?.message || "Failed to load inventory."}
        </div>
      ) : assets.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <Search className="mx-auto h-6 w-6 text-muted-foreground" />
          <h3 className="mt-2 text-base font-medium">No assets found</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {assetTypeFilter !== ALL || regionFilter !== ALL || postureFilter !== ALL
              ? "No assets match the current filters. Try clearing them."
              : "Run a discovery scan to populate your inventory."}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <section key={group.type}>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-sm font-semibold">
                  {ASSET_TYPE_LABELS[group.type]}
                </h2>
                <Badge variant="outline" className="text-xs">
                  {group.items.length}
                </Badge>
              </div>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40%]">Name</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Posture</TableHead>
                      <TableHead>Last seen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.items.map((asset) => (
                      <TableRow
                        key={asset.id}
                        className="cursor-pointer"
                        onClick={() => openAsset(asset.id)}
                      >
                        <TableCell className="max-w-0">
                          {/* Untrusted customer string — text only + truncated. */}
                          <div className="truncate font-medium" title={asset.name}>
                            {truncate(asset.name, 120)}
                          </div>
                          <div
                            className="truncate font-mono text-xs text-muted-foreground"
                            title={asset.arn_or_native_id}
                          >
                            {truncate(asset.arn_or_native_id, 120)}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {truncate(asset.region, 30)}
                        </TableCell>
                        <TableCell>
                          {asset.posture_flags.length === 0 ? (
                            <span className="text-xs text-muted-foreground">Clean</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {topSeverities(asset).map((sev) => (
                                <PostureFlagBadge key={sev} severity={sev} />
                              ))}
                              {asset.posture_flags.length > 1 && (
                                <span className="text-xs text-muted-foreground">
                                  {asset.posture_flags.length} flags
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(asset.last_seen_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          ))}
        </div>
      )}

      <AwsDiscoveryAssetDetailSheet
        assetId={selectedAssetId}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </div>
  );
}

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

/**
 * Return the distinct severities present on an asset, highest first, capped at
 * the two most severe for compact table display. Posture flags are short code
 * strings; the severity is derived from each code.
 */
function topSeverities(asset: DiscoveredAsset): PostureSeverity[] {
  const distinct = Array.from(
    new Set(asset.posture_flags.map((code) => postureFlagSeverity(code)))
  );
  distinct.sort((a, b) => (SEVERITY_RANK[a] ?? 99) - (SEVERITY_RANK[b] ?? 99));
  return distinct.slice(0, 2);
}
